import {
    BufferingOverlay,
    Button,
    ButtonConfig,
    Component,
    Container,
    ControlBar,
    FullscreenToggleButton,
    PlaybackTimeLabel,
    PlaybackTimeLabelMode,
    PlaybackToggleOverlay,
    Spacer,
    UIContainer,
    VolumeSlider,
    VolumeToggleButton,
    AdClickOverlay,
    PlayerUtils,
    Label,
    TitleBar,
} from 'bitmovin-player-ui';
import type { PlayerAPI } from 'bitmovin-player';
import { __ } from '../../../translations';
import { getReduxGlobal } from '../../../helpers/common';
import { showUpgradedModal } from '../../../store/reducers/generalConfigurationSlice';
import playerService from '../../../helpers/bitmovinPlayer';
import { avodMixpanelEvents } from '../../../helpers/avodHandler';
import { isGuestUser } from '../../../config/guestMode';
import user from '../../../helpers/user';

/** No Ads CTA (Call to Action): upgrade button shown during ads. Config: avodConfigs.noAdCta.byAdType */
let noAdCtaButtonRef: Button<ButtonConfig> | null = null;
let noAdCtaAdBreakStartTimeRef: number | null = null;

/** Bitmovin player instance for outstream banner (separate from main VOD player). Used for No Ads CTA / Youbora. */
let outstreamPlayerForNoAdCtaRef: PlayerAPI | null = null;

let outstreamActiveAdIdRef = '';

export const setOutstreamPlayerForNoAdCta = (player: PlayerAPI | null) => {
    outstreamPlayerForNoAdCtaRef = player;
};

export const getOutstreamPlayerForNoAdCta = () => outstreamPlayerForNoAdCtaRef;

export const setOutstreamActiveAdIdForNoAdCta = (adId: string) => {
    outstreamActiveAdIdRef = adId || '';
};

export const getOutstreamActiveAdIdForNoAdCta = () => outstreamActiveAdIdRef;

export const getNoAdCtaButton = () => noAdCtaButtonRef;
export const setNoAdCtaAdBreakStartTime = (time: number | null) => {
    noAdCtaAdBreakStartTimeRef = time;
};
export const getNoAdCtaAdBreakStartTime = () => noAdCtaAdBreakStartTimeRef;
export const setNoAdCtaButton = (btn: Button<ButtonConfig> | null) => {
    noAdCtaButtonRef = btn;
};

export type NoAdCtaConfig = {
    enabled?: boolean;
    maxUpgradeStaySeconds?: number | string;
    byAdType?: Record<string, boolean>;
};

/**
 * Show or hide No Ads CTA based on CMS config and ad type.
 * Used by both SSAI (VOD) and CSAI (Live/Catchup).
 * @param config - Full app config (avodConfigs.noAdCta.byAdType)
 * @param adType - 'preroll' | 'midroll' | 'postroll'
 */
export const setupNoAdCtaFromConfig = (
    config: { avodConfigs?: { noAdCta?: NoAdCtaConfig } } | null,
    adType: string
) => {
    hideNoAdCta();
    if (isGuestUser(user.getUserInfo())) {
        return;
    }

    const baseConfig: NoAdCtaConfig = config?.avodConfigs?.noAdCta || {};
    if (!adType || !baseConfig.byAdType) {
        return;
    }

    const key = adType.toLowerCase();
    const isEnabledGlobally = baseConfig.enabled ?? false;
    const isEnabled = isEnabledGlobally ? baseConfig.byAdType[adType] ?? baseConfig.byAdType[key] : false;

    if (!isEnabled) {
        return;
    }

    getNoAdCtaButton()?.show();
};

export const hideNoAdCta = () => getNoAdCtaButton()?.hide();

export const triggerNoAdCtaAction = (opts?: { isOutstream?: boolean }) => {
    const isOutstream = opts?.isOutstream === true;
    const outstreamStoredAdId = isOutstream ? getOutstreamActiveAdIdForNoAdCta() : '';
    const outstreamActiveAd = isOutstream
        ? (
              outstreamPlayerForNoAdCtaRef as unknown as { ads?: { getActiveAd?: () => unknown } }
          )?.ads?.getActiveAd?.() ?? null
        : null;
    const outstreamAdIdFromPlayer =
        isOutstream && outstreamActiveAd && typeof outstreamActiveAd === 'object'
            ? String(
                  (outstreamActiveAd as Record<string, unknown>).creativeId ??
                      (outstreamActiveAd as Record<string, unknown>).id ??
                      ''
              )
            : '';
    const outstreamAdId = outstreamStoredAdId || outstreamAdIdFromPlayer || '';

    avodMixpanelEvents.onNoAdCtaClick({
        source: isOutstream ? 'Ad' : undefined,
        contentType: isOutstream ? 'Outstream' : undefined,
        adType: isOutstream ? 'outstream' : undefined,
        adId: isOutstream ? outstreamAdId : undefined,
    });

    const player = playerService.getPlayer();

    if (player && player.isPlaying()) {
        player.pause();
    } else if (outstreamPlayerForNoAdCtaRef && outstreamPlayerForNoAdCtaRef.isPlaying()) {
        outstreamPlayerForNoAdCtaRef.pause();
    }
    getReduxGlobal().dispatch(showUpgradedModal({ open: true, fromNoAdsCta: true, noAdsCtaSource: 'Ad' }));
};

/** True if (clientX, clientY) is inside No Ads CTA bounds. Used when overlay blocks CTA in fullscreen. */
export const isClickOnNoAdCta = (clientX: number, clientY: number): boolean => {
    const adsUi = document.getElementById('ads-ui');
    if (!adsUi) return false;
    const cta = adsUi.querySelector('.ui-no-ad-cta-button') as HTMLElement;
    if (!cta || cta.classList.contains('bmpui-hidden')) return false;
    const rect = cta.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
};

export const createAdsUI = (isOutstreamAd?: boolean) => {
    const controlBarComponents = [];

    let noAdCtaButton: Button<ButtonConfig> | null = null;
    if (!isGuestUser(user.getUserInfo())) {
        noAdCtaButton = new Button({
            cssClass: 'button-with-text ui-no-ad-cta-button bmpui-ui-button',
        });
        noAdCtaButton.setText(__('no_ads_cta'));
        noAdCtaButton.onClick.subscribe(() => triggerNoAdCtaAction({ isOutstream: Boolean(isOutstreamAd) }));
        noAdCtaButton.hide();
    }
    setNoAdCtaButton(noAdCtaButton);

    if (isOutstreamAd) {
        controlBarComponents.push(
            new Container({
                components: [
                    new Container({
                        components: [
                            new Label({
                                text: `<span id="currentAdNumber"></span> ${__(
                                    'player_ads_count'
                                )} <span id="totalAdsCount"></span>`,
                                cssClasses: ['ui-ads-label'],
                            }),
                        ],
                        cssClasses: ['ui-ads-status', 'ui-ads-status-outstream'],
                    }),
                    new Spacer(),
                    ...(noAdCtaButton ? [noAdCtaButton] : []),
                ],
                cssClasses: ['controlbar-bottom'],
            })
        );
    } else {
        controlBarComponents.push(
            new Container({
                id: 'custom-ad-seekbar-wrapper',
                cssClasses: ['custom-ad-seekbar-wrapper'],
                components: [
                    new Container({
                        id: 'custom-ad-seekbar-track',
                        cssClasses: ['custom-ad-seekbar-track'],
                        components: [
                            new Container({
                                id: 'custom-ad-seekbar-progress',
                                cssClasses: ['custom-ad-seekbar-progress'],
                            }),
                        ],
                    }),
                ],
            }),
            new Container({
                components: [
                    new VolumeToggleButton(),
                    new VolumeSlider(),
                    new Container({
                        components: [
                            new Label({
                                text: `<span id="currentAdNumber"></span> ${__(
                                    'player_ads_count'
                                )} <span id="totalAdsCount"></span>`,
                                cssClasses: ['ui-ads-label'],
                            }),
                            new Container({
                                components: [
                                    new Label({
                                        text: `<span id="currentAdsTime"></span>`,
                                        cssClasses: ['ui-ads-time'],
                                    }),
                                    new Label({
                                        text: ` / <span id="totalAdsTime"></span>`,
                                        cssClasses: ['ui-ads-time'],
                                    }),
                                ],
                                cssClass: 'ui-ads-playbacktime',
                            }),
                        ],
                        cssClass: 'ui-ads-status',
                    }),
                    new Spacer({ id: 'ads-ui-spacer' }),
                    new FullscreenToggleButton(),
                ],
                cssClasses: ['controlbar-bottom'],
            })
        );
    }

    const controlBar = new ControlBar({
        components: controlBarComponents,
    });

    return new UIContainer({
        id: 'ads-ui',
        components: [
            new BufferingOverlay({
                components: [
                    new Component({
                        tag: 'div',
                        cssClass: 'ui-buffering-overlay-indicator',
                        role: 'img',
                    }),
                ],
            }),
            new AdClickOverlay(),
            new PlaybackToggleOverlay(),
            isOutstreamAd
                ? new TitleBar({
                      components: [
                          new Container({
                              components: [
                                  new Label({
                                      cssClass: 'current-ad-time-in-seconds',
                                      text: `<div class="circle"><div id="currentAdTimeInSeconds"></div></div>`,
                                  }),
                                  new Spacer(),
                                  new VolumeToggleButton(),
                              ],
                              cssClasses: ['controlbar-top'],
                          }),
                      ],
                  })
                : new Container({}),
            controlBar,
            !isOutstreamAd && noAdCtaButton
                ? new Container({
                      components: [
                          new Container({
                              components: [noAdCtaButton],
                              cssClasses: ['skip-outro-buttons'],
                          }),
                      ],
                      cssClasses: ['skip-container', 'ui-no-ad-cta-container'],
                  })
                : new Container({}),
        ],
        cssClasses: ['ui-skin-ads', isOutstreamAd ? 'outstream-ads' : ''],
        hideDelay: -1,
        hidePlayerStateExceptions: [
            PlayerUtils.PlayerState.Prepared,
            PlayerUtils.PlayerState.Paused,
            PlayerUtils.PlayerState.Finished,
        ],
    });
};
